/**
 * BYO-specific step 3: renders FlexFlow's FileSystem (folder browser + Folder
 * Filters + File Preview + MonitoringInterval/schedule) and on "Create" calls
 * buildByoSourcePayload (which creates the code container) then saves the source.
 */

import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import useAppDispatch from 'js/hooks/useAppDispatch';

import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { addDataSource } from 'js/actions/sources';
import { loadDataSource, addDetectedDataset } from 'js/actions';
import { createCodeContainer } from 'js/actions/transform';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';

import { FileSystem } from 'new/components/flows/configure/FileSystem';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import useConfig from 'new/components/flows/types/DirectFlow/hooks/useConfig';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useForm } from 'new/elements/Form/useForm';
import { useHeaderMetaConfig } from 'new/components/flows/context/useHeaderMetaConfig';
import {
  buildByoSourcePayload,
  sanitizeSourceConfigForSave,
} from 'new/components/flows/types/byo/v2/persistence';
import { withByoRuntimeMode } from 'new/components/flows/types/byo/core/model';
import useFlowParams from 'new/components/flows/useFlowParams';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import { useStudioDraftCtx } from '../studio/StudioDraftContext';
import type { FCFormPageType } from 'new/elements/Form/useForm';
import {
  ByoConfigureStepFrame,
  createOnceOrUpdate,
  credentialPatch,
  useByoConfigureStepSave,
} from './ByoConfigureStepFrame';

const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;

export const ByoSourceConfigureStep: FCFormPageType<{}> = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { nextPage } = useForm();
  const { sourceConfig, onSourceConfigChanged } = useConfig();
  const { onBucketSelected } = useFlowsSetup(FORM, 'custom');
  const { selectedConnection, credentialId, selectedBucket, sourceConfigFields, source } =
    useDirectFlow();
  const { name, description } = useHeaderMetaConfig();
  const { id: editId, onDismiss: flowOnDismiss } = useFlowParams();
  const { requestUpdateSourceById } = useSourcesAdapter();
  const { draft, sourceCreateProgressRef } = useStudioDraftCtx();
  const draftName = draft.name;

  const onSave = useCallback(async () => {
    if (!selectedConnection || !credentialId) {
      throw new Error('Connection and credential are required.');
    }
    // Edit mode: update source_config only; do NOT recreate the code container.
    if (editId) {
      // useDirectFlow/useConfig hydrate sourceConfig asynchronously. A Save
      // click before hydration lands would fall through to `sourceConfig ??
      // {}` below and updateSourceInfo does a full replace (not a merge) for
      // a generic connector — wiping the source's real folder/filter/
      // schedule config down to just `{ path }`. Bail until hydration has
      // populated sourceConfig, same race + fix as the sibling gate in
      // ByoDestinationConfigureStep.tsx. Confirmed bug, fixed 2026-08-20.
      if (!sourceConfig) return;
      // selectedBucket is a Bucket object {id,path,...}; extract the string path.
      const bucketPath =
        (selectedBucket as any)?.path ?? selectedBucket ?? (sourceConfig as any)?.path ?? '';
      // `sourceConfig` here is useStartSource's hydrated form state, not the
      // persisted source_config — it carries UI-only convenience fields
      // (decoded code, sourceName/sourceDescription/flow_triggers) that
      // never belong in source_config. sanitizeSourceConfigForSave is the
      // one function both BYO v2 save paths (this one and
      // useByoSourceSave.ts) run source_config through before persisting —
      // this edit branch used to send `sourceConfig` straight through
      // unsanitized, leaking the raw decoded Python source into
      // source_config on every save. Confirmed bug, fixed 2026-08-31.
      // FileSystem's name/description inputs write to sourceConfig.sourceName
      // / sourceDescription (useStartSource hydrates them from the source),
      // which sanitizeSourceConfigForSave strips from source_config — so
      // they must be sent as the top-level fields or a rename is lost.
      const editedName = (sourceConfig as any)?.sourceName?.trim?.();
      const editedDescription = (sourceConfig as any)?.sourceDescription;
      await requestUpdateSourceById(Number(editId), {
        ...(editedName ? { name: editedName } : {}),
        ...(typeof editedDescription === 'string' ? { description: editedDescription } : {}),
        flow_type: 'custom',
        // This edit branch never goes through buildByoSourcePayload, so the
        // runtime mode is re-added here — sources saved before 2026-09-01
        // (when it was being stripped) would otherwise stay missing it.
        source_config: withByoRuntimeMode({
          ...sanitizeSourceConfigForSave((sourceConfig ?? {}) as any),
          path: typeof bucketPath === 'string' ? bucketPath : ((bucketPath as any)?.path ?? ''),
        }),
      });
      flowOnDismiss?.({});
      return true;
    }
    // Name typed in this step (FileSystem → sourceConfig.sourceName) >
    // canvas "Flow name" input > DirectFlow header name > selected directory name.
    const dirName =
      (sourceConfig as any)?.path?.split('/')?.filter(Boolean)?.pop() ??
      (selectedBucket as any)?.path?.split('/')?.filter(Boolean)?.pop() ??
      '';
    const stepName = (sourceConfig as any)?.sourceName?.trim?.();
    const flowName = stepName || draftName || name || dirName || '';
    // Retry-safe: every step below records what it persisted in the draft's
    // progress ref, so a Continue retried after a LATER step failed (e.g.
    // loadDataSource) resumes from the failed step instead of POSTing a
    // duplicate source/nexset. The ref lives in StudioDraftProvider (above
    // the Form), so it also survives this step unmounting on Back.
    const progress = sourceCreateProgressRef.current;
    const payload = await buildByoSourcePayload(
      {
        input: {
          connectionType: selectedConnection,
          credentialId,
          folderPath: null,
          selectedFile: null,
          fileType: null,
        },
        // predefined + null id → ensureCodeContainerId returns null, no container created.
        // The code container is created in Step 2 when the user configures code.
        code: { mode: 'predefined', code: '', predefinedId: null, packages: [], extraData: '' },
        sourceConfigArgs: {
          sourceConfig,
          configFields: sourceConfigFields ?? [],
          selectedConnection,
        } as any,
        name: flowName,
        description: (sourceConfig as any)?.sourceDescription || description,
      },
      { dispatch: dispatch as any, createCodeContainerAction: createCodeContainer as any },
    );
    const sourceInfo = {
      name: payload.name,
      description: payload.description,
      flow_type: payload.flow_type,
      code_container_id: payload.code_container_id,
      source_config: payload.source_config,
    };
    const credential = credentialPatch(credentialId);
    // On a changed retry the source already created is updated in place —
    // its nexset, if any, stays attached.
    const newId = await createOnceOrUpdate(
      progress,
      'sourceId',
      [selectedConnection, credential.data_credentials_id, sourceInfo],
      {
        create: async () => {
          const res: any = await dispatch(
            addDataSource(selectedConnection, null, sourceInfo, credentialId) as any,
          );
          return res?.result ?? res?.response?.id ?? res?.payload?.id ?? res?.id ?? null;
        },
        update: (sourceId) =>
          requestUpdateSourceById(Number(sourceId), {
            ...sourceInfo,
            source_type: selectedConnection,
            ...credential,
          }),
        createFailedMessage: 'The BYO flow source could not be created.',
      },
    );

    // Create the nexset now (no activation needed — addDetectedDataset POSTs
    // to /data_sets with data_source_id and returns the new nexset id). This
    // is a real, deliberately-hidden entity: BYO is source→code→destination
    // with no user-facing nexset concept, but the backend/canvas graph (and
    // the destination step, which needs a data_set_id to attach a sink to)
    // still require one to exist. Regression, confirmed live 2026-08-23:
    // a 2026-08-20 session removed this call, assuming the deferred
    // activation in ByoDestinationConfigureStep.tsx would surface a nexset
    // via `source.data_sets[0]` once the source activates there instead —
    // it never does for a custom-processor source (confirmed: the real
    // source entity has no `data_sets` field at all), so every "Add
    // Destination" attempt after that regression silently no-opped.
    //
    // A failure here used to be swallowed as "non-fatal", relying on that
    // same destination-step fallback the comment above shows never works —
    // the user got a "created" flow whose destination step then failed.
    // Surface it instead; the source is already recorded in `progress`, so
    // the next Continue retries only this call. NEX-22636.
    if (progress.nexsetId == null) {
      let dsRes: any;
      try {
        dsRes = await dispatch((addDetectedDataset as any)({ name: flowName }, Number(newId)));
      } catch (e: any) {
        throw new Error(
          `The flow source was created, but its dataset could not be: ${
            e?.message ?? 'unknown error'
          }. Click Continue to retry.`,
        );
      }
      if (dsRes?.result == null) {
        throw new Error(
          'The flow source was created, but its dataset could not be. Click Continue to retry.',
        );
      }
      progress.nexsetId = dsRes.result;
    }
    const nexsetId = progress.nexsetId;

    const loaded: any = await dispatch(loadDataSource(Number(newId)) as any);
    const src = loaded?.entities?.sources?.[loaded?.result];
    const flowNodeId = src?.origin_node_id ?? src?.flow_node_id ?? null;

    // Return to the BYO canvas with the new source info — don't navigate away.
    // Fall back to navigating to the production canvas only if no parent canvas handler.
    if (flowOnDismiss) {
      flowOnDismiss({
        sourceId: newId,
        flowNodeId,
        name: flowName,
        connector: selectedConnection,
        nexsetId,
      });
    } else if (flowNodeId) {
      history.replace(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/${flowNodeId}/edit`);
    }
    return true;
  }, [
    selectedConnection,
    credentialId,
    sourceConfig,
    sourceConfigFields,
    name,
    draftName,
    description,
    dispatch,
    history,
    editId,
    flowOnDismiss,
    requestUpdateSourceById,
    selectedBucket,
    sourceCreateProgressRef,
  ]);

  const saveError = useByoConfigureStepSave(onSave, 'Failed to create the BYO flow.');

  return (
    <ByoConfigureStepFrame hydrating={Boolean(editId) && !sourceConfig} saveError={saveError}>
      <FileSystem
        formType={FORM}
        shouldShowLoading
        flowType="custom"
        connectionType="SOURCE"
        source={source}
        selectedBucket={selectedBucket}
        selectedConnection={selectedConnection}
        credentialId={credentialId}
        sourceConfig={sourceConfig}
        onBucketSelected={onBucketSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onSaveAdvancedMode={onSourceConfigChanged}
        nextFormStage={nextPage}
      />
    </ByoConfigureStepFrame>
  );
};

ByoSourceConfigureStep.formConfig = { title: 'Configure' };

export default ByoSourceConfigureStep;
